/* eslint-disable react/no-multi-comp */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  MapContainer,
  LayersControl,
  TileLayer,
  GeoJSON,
  Pane
} from 'react-leaflet';
import { balbrigganCoords, cellNameCoords } from '../../constants';
import { connect, useSelector, useDispatch } from 'react-redux';
import { getCellName } from '../../selectors/ordersSelector';
import { geofenceColor, indexPriority } from 'utils/ui';
import { RootState } from 'store/configureStore';
import { getGeofences } from 'actions/geofenceActions';
import { OrdersMapContents } from './OrdersMap';
import {
  getAddressLocations,
  updateAddress
} from 'actions/routePlannerActions';
import MarkerClusterGroup from 'react-leaflet-cluster';
import ClickableMarkerForHomes from './components/ClickableMarkerForHomes';
import ActiveHomeDialog from './components/ActiveHomeDialog';
import { DropLocation } from '@m4a3/api-client';

const { BaseLayer } = LayersControl;

export type Address = {
  id: string;
  address: string;
  address_latitude: number;
  address_longitude: number;
  cellId: number;
  devRouteName: any;
  dropLocation: DropLocation;
  dropLocationHistory: DropLocation[];
  eircode: string;
  enabled: boolean;
  generatedCellId: number;
  idString: string;
  km_distance: number;
  misc1: any;
  misc2: any;
  mode: any;
  orderCount: number;
  orderId: any;
  preferredDropLocationId: number;
  zone: any;
};

const SelectedCellMap = ({
  getGeofences,
  showOPSDetails,
  showAddressLocations,
  getAddressLocations,
  updateAddress
}) => {
  const mapRef = useRef(null);
  const showOPSDetailsOnMap = showOPSDetails ? showOPSDetails : false;
  const cellName = useSelector(getCellName);
  const geofences = useSelector(
    (state: RootState) => state.geofences.geofences
  );
  const initialLocation = cellNameCoords[cellName] || balbrigganCoords;
  const location = cellNameCoords[cellName];
  const [selectedAddress, setSelectedAddress] = useState<Address | null>();
  const [showSelectedAddressModal, setShowSelectedAddressModal] = useState(
    false
  );
  const dispatch = useDispatch();
  const INITIAL_ZOOM_OF_CAMERA = 15;

  const addresses = useSelector(
    (state: RootState) => state.routePlanner.userAddressLocations
  );

  const cellId = useSelector((state: RootState) => state.orders.selectedCellId);

  useEffect(() => {
    if (mapRef.current && location) {
      mapRef?.current?.flyTo(location, INITIAL_ZOOM_OF_CAMERA, {
        animate: false
      });
    }
    getGeofences();
    getAddressLocations(cellId);
  }, [location]);

  const handleMarkerClicked = useCallback(address => {
    setSelectedAddress(address);
    setShowSelectedAddressModal(true);
  }, []);

  const handleConfirm = (address, updatedEnabledValue) => {
    const temporaryAddress = address.enabled;
    address.enabled = updatedEnabledValue;
    updateAddress(address.id, address)
      .then(res => {})
      .catch(err => {
        address.enabled = temporaryAddress;
        setSelectedAddress(address);
      });
    onClose();
  };

  function onClose() {
    setShowSelectedAddressModal(false);
    setSelectedAddress(null);
  }

  function handleChangeCoordsForAddress(address, lat, lng) {
    const addressToUpdate = address;
    addressToUpdate.address_latitude = lat;
    addressToUpdate.address_longitude = lng;
    updateAddress(addressToUpdate.id, addressToUpdate);
  }

  const getAddresses = () => {
    if (showAddressLocations) {
      return (
        <MarkerClusterGroup chunkedLoading>
          {addresses?.map(address => {
            return (
              <ClickableMarkerForHomes
                key={address.id}
                address={address}
                setSelectedAddress={setSelectedAddress}
                handleMarkerClicked={() => handleMarkerClicked(address)}
                handleConfirm={handleConfirm}
                handleChangeCoords={(lat, lng) =>
                  handleChangeCoordsForAddress(address, lat, lng)
                }
              />
            );
          })}
        </MarkerClusterGroup>
      );
    } else {
      return <></>;
    }
  };

  const GeofenceBorder = () => {
    if (geofences && geofences.length > 0) {
      const uiGeofences = geofences
        .filter(geofence => geofence.active === true)
        .map(geofence => {
          return (
            <GeoJSON
              data={geofence.geoJson}
              style={{ fill: false, color: geofenceColor(geofence.id) }}
              key={geofence.id}
            />
          );
        });

      return (
        <Pane
          key="Geofences"
          name="Geofences"
          style={{ zIndex: indexPriority(9) }}>
          {uiGeofences}
        </Pane>
      );
    }
    return <></>;
  };

  return (
    <div className={'w-full h-full'} key="OrderMap">
      <MapContainer
        key="OrderMapMap"
        center={initialLocation}
        style={{ height: '100%', width: '100%' }}
        zoom={14}
        ref={mapRef}>
        <TileLayer
          attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxNativeZoom={19}
          maxZoom={22}
        />
        <TileLayer
          attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />
        <LayersControl>
          <BaseLayer checked name="Manna v1">
            <TileLayer
              attribution="Manna-v1"
              url="https://tiles.manna.aero/{z}/{x}/{y}.png"
              maxZoom={22}
            />
          </BaseLayer>
          <BaseLayer name="Manna v2">
            <TileLayer
              attribution="Manna-v2"
              url="https://tiles-dev.manna.aero/{z}/{x}/{y}.png"
              maxZoom={22}
            />
          </BaseLayer>
        </LayersControl>
        <GeofenceBorder />
        {getAddresses()}
        {showOPSDetailsOnMap && (
          <OrdersMapContents mapRef={mapRef}></OrdersMapContents>
        )}
      </MapContainer>

      <ActiveHomeDialog
        modalOpen={showSelectedAddressModal}
        onClose={onClose}
        handleConfirm={handleConfirm}
        address={selectedAddress}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  geofences: state.geofences.geofences,
  userAddressLocations: state.routePlanner.userAddressLocations,
  selectedCellId: state.orders.selectedCellId
});

const mapDispatchToProps = dispatch => ({
  getGeofences: () => dispatch(getGeofences()),
  getAddressLocations: cellId => dispatch(getAddressLocations(cellId)),
  updateAddress: (addressId, address) =>
    dispatch(updateAddress(addressId, address))
});

export default connect(mapStateToProps, mapDispatchToProps)(SelectedCellMap);
